// Usage activity for the admin page (see the Privacy Policy, "Usage activity"): page views, tabs, toggles, panels
// and dialogs opened, actions, load times and errors. Batched and sent to POST /api/rum every 15 seconds and when the
// tab is hidden; only for signed-in users, and never what people type, the odds they enter or AI questions.
import { useEffect, useRef } from 'react';
import { getAccessToken, hasSignedInUser } from '../services/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

export type TrackName = 'page_view' | 'tab' | 'toggle' | 'open' | 'action' | 'load' | 'error';

interface TrackEvent {
  t: number;
  name: TrackName;
  detail?: string;
  page: string;
  ms?: number;
}

const FLUSH_MS = 15_000;
const MAX_BATCH = 40;

const sessionId =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

let queue: TrackEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

const flush = async (): Promise<void> => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const batch = queue.splice(0, 50);
  if (!hasSignedInUser()) return; // signed out: nothing is recorded
  const token = await getAccessToken();
  if (!token) return;
  try {
    // keepalive lets the request finish even as the page closes (sendBeacon can't send the Authorization header)
    await fetch(`${API_URL}/api/rum`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ sessionId, events: batch }),
    });
  } catch {
    // usage events are best-effort
  }
};

/** Records a usage event. detail is a short label (a tab, a track, a feature), never user input. */
export const track = (name: TrackName, detail?: string, ms?: number): void => {
  queue.push({
    t: Date.now(),
    name,
    detail: detail?.slice(0, 80),
    page: typeof window !== 'undefined' ? window.location.pathname : '',
    ms: ms !== undefined ? Math.round(ms) : undefined,
  });
  if (queue.length >= MAX_BATCH) void flush();
  else if (!timer) timer = setTimeout(() => void flush(), FLUSH_MS);
};

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
  // How long the first page took to load, once per visit.
  window.addEventListener('load', () => {
    setTimeout(() => {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (nav && nav.duration > 0) track('load', 'page', nav.duration);
    }, 0);
  });
  // Uncaught errors (the message only, cut short).
  window.addEventListener('error', (e) => track('error', String(e.message || 'error').slice(0, 80)));
  window.addEventListener('unhandledrejection', (e) =>
    track('error', String((e.reason as Error)?.message ?? e.reason ?? 'rejection').slice(0, 80)),
  );
}

/**
 * Records `name` with a label when `value` changes (not on the first render), e.g. a tab or a toggle:
 * useTrackChange('tab', resultsTab). Return null from label to skip a value (e.g. a panel closing).
 */
export const useTrackChange = <T>(
  name: TrackName,
  value: T,
  label: (v: T) => string | null = (v) => String(v),
): void => {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const l = label(value);
    if (l !== null) track(name, l);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
};
